import type { IPointerController, IPointerHandler } from '@/components/viewPort/PointerController';
import type { PointerSyntheticEvent } from '@/components/viewPort/PointerSyntheticEvent';
import type {
  IBaseViewModel,
  ICenterLineViewModel,
  IObjectViewModel,
  IPickable,
  IViewItemsViewModelController
} from '@/engine/app/interfaces';
import { SceneItemKind } from '@/engine/app/interfaces';
import type { Ray, Vector3 } from 'three';
import { iocContainer, TYPES } from '@/engine/ioc';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { ViewsService } from '@/engine/app/Views.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { AreaMeasurementBasePlaneTuner } from '@/engine/PlanningAlgorithm/Tuners/AreaMeasurementBasePlaneTuner';
import type { ViewController } from '@/engine/app/View.controller';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import type { SliceType } from '@/engine/app/modules/images/interfaces';

export interface IPickablePolyLinePresentation {
  getPickableLinesCoords: () => Array<[Vector3, Vector3]>;
  readonly lineWidth: number;
}
export class PolyLineHighlighter implements IPickable, IPointerHandler {
  private _parent?: IPointerController;

  private _viewModel: ICenterLineViewModel;

  private _presentation: IPickablePolyLinePresentation;

  private _imagesService: ImagesService;

  private _viewsService: ViewsService;

  private _viewController: ViewController;

  private _controller: IViewItemsViewModelController;

  private _customPlaneVM: IObjectViewModel | undefined;

  private _planningAlgorithm: PlanningAlgorithm;

  private readonly _planeTuner: AreaMeasurementBasePlaneTuner;

  private readonly _commandProcessor: ICommandProcessor;

  public constructor(
    viewModel: ICenterLineViewModel,
    presentation: IPickablePolyLinePresentation,
    imagesService: ImagesService,
    viewsService: ViewsService,
    viewController: ViewController,
    planningAlgorithm: PlanningAlgorithm,
    commandProcessor: ICommandProcessor
  ) {
    this._viewModel = viewModel;
    this._presentation = presentation;
    this._imagesService = imagesService;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._commandProcessor = commandProcessor;

    this._controller = iocContainer.get<IViewItemsViewModelController>(TYPES.IViewItemsViewModelController);

    this._customPlaneVM = this._viewsService.currentView
      ?.getObjects()
      .find((object) => object.kind === SceneItemKind.CustomShiftingPlane);

    this._planningAlgorithm = planningAlgorithm;
    this._planeTuner = new AreaMeasurementBasePlaneTuner(this._planningAlgorithm);
  }

  public setParent(parent: IPointerController): void {
    this._parent = parent;
  }

  public getViewModel(): IBaseViewModel | undefined {
    return undefined;
  }

  public getPickedDistance(event: PointerSyntheticEvent): number | undefined {
    const lineDistances = this._presentation
      .getPickableLinesCoords()
      .map(([pointFrom, pointTo]: [Vector3, Vector3]) => {
        return Math.sqrt(event.ray.distanceSqToSegment(pointFrom, pointTo));
      });

    if (lineDistances.some((d) => d <= this._presentation.lineWidth * 2)) {
      return Math.min(...lineDistances);
    }

    return undefined;
  }

  public onPicked(event: PointerSyntheticEvent, parent: IPointerController | undefined): IPointerHandler | undefined {
    this._viewModel.setHighlighting(true);
    parent?.setCursor(this._getCursor());
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public onPointerDown(event: PointerSyntheticEvent): Promise<void> {
    if (this.getPickedDistance(event) === undefined) return Promise.resolve();
    if (this._customPlaneVM?.attachments.at(0) === undefined) return Promise.resolve();
    event.stopPropagation();
    this._parent?.setPointerCapture(event.originalEvent.pointerId);

    this._viewModel.setHighlighting(false);

    const nearestSegment = this._getPickedSegment(event.ray, this._presentation.getPickableLinesCoords());

    const descriptor = this._customPlaneVM.attachments[0];
    const { normal: undoNormal, origin: undoOrigin } = this._planeTuner.getValue(descriptor);
    const redoOrigin = nearestSegment[0];
    const redoNormal = nearestSegment[1].clone().sub(redoOrigin).normalize();

    const redoCenterlineDescriptor = this._viewModel.descriptor;
    const undoCenterlineDescriptor =
      this._viewsService.currentViewOrThrow.getActiveCenterLineDescriptor() ?? redoCenterlineDescriptor;

    const alongSliceDescriptor = this._viewModel.alongSliceDescriptor;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.executeAnonymousCommand(
      async (): Promise<undefined> => {
        await this._updatePlane(descriptor, redoNormal, redoOrigin, redoCenterlineDescriptor, alongSliceDescriptor);
      },
      async (): Promise<undefined> => {
        await this._updatePlane(descriptor, undoNormal, undoOrigin, undoCenterlineDescriptor, alongSliceDescriptor);
      }
    );

    return Promise.resolve();
  }

  public onPointerUp(event: PointerSyntheticEvent): void {
    if (this.getPickedDistance(event) === undefined) return;
    event.stopPropagation();
  }

  public onUnPicked(parent?: IPointerController): void {
    parent?.setCursor('');
    this._viewModel.setHighlighting(false);
  }

  private _getCursor(): string {
    return 'pointer';
  }

  private async _updatePlane(
    descriptor: string,
    normal: Vector3,
    origin: Vector3,
    centerlineDescriptor: string,
    alongSliceDescriptor: SliceType
  ): Promise<void> {
    await this._planeTuner.setValueAsync(descriptor, normal, origin);
    this._viewController.setActiveCenterLineDescriptor(centerlineDescriptor);
    this._imagesService.requestSliceByPoint(alongSliceDescriptor, origin);
    this._controller.setViewModelSelection(descriptor, true);
  }

  private _getPickedSegment(ray: Ray, segments: Array<[Vector3, Vector3]>): [Vector3, Vector3] {
    const pickedSegment = segments.find(([pointFrom, pointTo]: [Vector3, Vector3]) => {
      if (Math.sqrt(ray.distanceSqToSegment(pointFrom, pointTo)) <= this._presentation.lineWidth * 2) {
        return true;
      }
      return false;
    });
    if (pickedSegment === undefined) {
      throw new Error("Can't find picked segment");
    }

    return pickedSegment;
  }
}
